<template>
  <div class="challenge-modifier" :class="{
    'negative-tint': negativeTint,
    'highlighted': props.isHighlighted,
    'animating': props.isAnimating
  }">
    <img
        v-if="iconUrl"
        :src="iconUrl"
        :alt="props.modifier.name + ' icon'"
        class="modifier-icon"
        @error="onImageError"
    />
    <div class="modifier-value">{{ formatedValue }}</div>
    <div class="modifier-name">{{ props.modifier.name }}</div>
  </div>
</template>

<script setup lang="ts">
import {computed, ref} from 'vue'
import DefaultPositiveImage from "@/assets/icons/challenge/artifact2.png"
import DefaultNegativeImage from "@/assets/icons/challenge/danger-artifact.png"
import {type ChallengeModifier} from "@/api/dx-backend";

interface Props {
  modifier: ChallengeModifier;
  isHighlighted?: boolean;
  isAnimating?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isHighlighted: false,
  isAnimating: false
})


const onImageError = (event: Event) => {
  console.warn(`Failed to load icon for ${props.modifier.id} modifier:`, props.modifier.icon)
}
const iconUrl = computed(() => {
  return props.modifier.icon || (props.modifier.value < 0 ? DefaultNegativeImage : DefaultPositiveImage)
})

const negativeTint = computed(() => {
  return props.modifier.value < 0
})

const formatedValue = computed(() => {
  return props.modifier.value > 0 ? `+${props.modifier.value}` : `${props.modifier.value}`
})

</script>

<style scoped>
.challenge-modifier {
  --flow: #7fe4ff;
  --bane: #ff8a7a;

  background: linear-gradient(180deg, rgba(18, 34, 44, 0.85) 0%, rgba(6, 11, 15, 0.92) 100%);
  border: 1px solid rgba(127, 228, 255, 0.28);
  border-radius: 2px;
  padding: 0.2em;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 0.5em;
  width: clamp(60px, 5vw, 120px);
  height: clamp(90px, 7.5vw, 180px);
  min-width: 60px;
  min-height: 90px;
  position: relative;
  transition: border-color 200ms ease, box-shadow 200ms ease, transform 200ms ease;
  box-shadow: inset 0 1px 0 rgba(127, 228, 255, 0.1);
  font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
  color: var(--flow);
}

.challenge-modifier:hover {
  border-color: rgba(127, 228, 255, 0.65);
  box-shadow: 0 0 18px rgba(127, 228, 255, 0.18), inset 0 1px 0 rgba(127, 228, 255, 0.2);
  transform: translateY(-2px);
}

.modifier-value {
  font-size: clamp(0.9rem, 1.2em, 1.5rem);
  font-weight: 200;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 0 14px currentColor;
  letter-spacing: 0.12em;
  padding-top: 1em;
  z-index: 1;
}

.modifier-icon {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5));
  mask: radial-gradient(circle at center, rgba(0, 0, 0, 1) 5%, rgba(0, 0, 0, 0) 100%);
  z-index: 0;
}

.modifier-name {
  z-index: 1;
  font-size: clamp(0.46rem, 0.62em, 0.82rem);
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-align: center;
  color: #93a8b3;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  line-height: 1.2;
  max-width: 100%;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.negative-tint {
  border-color: rgba(255, 138, 122, 0.3);
  color: var(--bane);
}

.negative-tint:hover {
  border-color: rgba(255, 138, 122, 0.7);
  box-shadow: 0 0 18px rgba(255, 138, 122, 0.18), inset 0 1px 0 rgba(255, 138, 122, 0.2);
  transform: translateY(-2px);
}

/* The active modifier surges with Flow as it is applied to the roll. */
.challenge-modifier.highlighted {
  animation: modifierSurge 0.8s ease-in-out;
  border-color: currentColor;
  box-shadow: 0 0 26px rgba(127, 228, 255, 0.35), inset 0 0 20px rgba(127, 228, 255, 0.12);
  transform: scale(1.08) translateY(-4px);
  z-index: 10;
}

.challenge-modifier.highlighted.negative-tint {
  box-shadow: 0 0 26px rgba(255, 138, 122, 0.35), inset 0 0 20px rgba(255, 138, 122, 0.12);
}

@keyframes modifierSurge {
  0% { transform: scale(1) translateY(0); }
  50% { transform: scale(1.12) translateY(-6px); }
  100% { transform: scale(1.08) translateY(-4px); }
}

/* Modifiers awaiting their turn recede rather than compete for attention. */
.challenge-modifier.animating:not(.highlighted) {
  opacity: 0.35;
  transition: opacity 0.3s ease;
}

@media (prefers-reduced-motion: reduce) {
  .challenge-modifier.highlighted {
    animation: none;
  }
}

</style>